import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Title,
  Card,
  Stack,
  Group,
  Button,
  Text,
  ActionIcon,
  Modal,
  TextInput,
  Switch,
  Badge,
  Loader,
  List,
  Tooltip,
} from '@mantine/core';
import { IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../components/ui/PageContainer';
import { api, ApiError, getErrorMessage } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { useTeamRoles } from '../../hooks/useReferenceApi';
import { useHasFeature } from '../../store/featureFlagsStore';
import { TeamRoleDef } from '../../types';
import { DEFAULT_ROLE_KEYS, teamRoleName } from '../../utils/teamRole';
import { usePermission } from '../../hooks/usePermission';

/** Тот же формат, что проверяет сервер: латиница, цифры, `-` и `_`, с буквы. */
const KEY_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;

export const TeamRoles: React.FC = () => {
  const { t } = useTranslation();
  const isOn = useHasFeature('teamRolesDictionary');
  const queryClient = useQueryClient();
  const { data: roles = [], isLoading } = useTeamRoles();
  const [editing, setEditing] = useState<TeamRoleDef | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [isGroup, setIsGroup] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Признак группы решает, кто пишет саммари, — его меняет только управляющий людьми.
  const canChangeGroup = usePermission('people.manage');
  const label = (role: TeamRoleDef) => teamRoleName(roles, role.key, t);

  // Сервер отвечает свежим списком — им и заменяем кэш, без лишнего запроса.
  const onSaved = (list: TeamRoleDef[], title: string) => {
    queryClient.setQueryData(['teamRoles'], list);
    showToast({ title, message: '', color: 'teal' });
    setIsModalOpen(false);
  };
  const onError = (fallback: string) => (error: unknown) =>
    showToast({
      title: t('common.error'),
      message: getErrorMessage(error, fallback),
      color: 'red',
    });

  const save = useMutation({
    mutationFn: async (): Promise<TeamRoleDef[]> =>
      editing
        ? api(`/team-roles/${editing.key}`, {
            method: 'PATCH',
            // Только то, что поменяли: роль по умолчанию без правки названия остаётся
            // «не переименованной», а isGroup без смены не требует people.manage.
            body: {
              ...(name.trim() !== label(editing) ? { name } : {}),
              ...(isGroup !== editing.isGroup ? { isGroup } : {}),
            },
          })
        : api('/team-roles', { method: 'POST', body: { key, name, isGroup } }),
    onSuccess: (list) => onSaved(list, editing ? t('teamRoles.saved') : t('teamRoles.added')),
    onError: onError(t('teamRoles.saveError')),
  });

  const remove = useMutation({
    mutationFn: async (role: TeamRoleDef): Promise<TeamRoleDef[]> =>
      api(`/team-roles/${role.key}`, { method: 'DELETE' }),
    onSuccess: (list) => {
      queryClient.setQueryData(['teamRoles'], list);
      showToast({ title: t('teamRoles.deleted'), message: '', color: 'teal' });
    },
    onError: (error, role) => {
      const teams = (error as ApiError)?.response?.data as {
        teams?: { _id: string; name: string }[];
      };
      if ((error as ApiError)?.status === 409 && teams?.teams?.length) {
        // Роль носят участники: показываем, в каких командах её сменить.
        modals.open({
          title: t('teamRoles.inUseTitle', { name: label(role) }),
          centered: true,
          children: (
            <Stack gap="sm">
              <Text size="sm">{t('teamRoles.inUseText')}</Text>
              <List size="sm">
                {teams.teams.map((team) => (
                  <List.Item key={team._id}>{team.name}</List.Item>
                ))}
              </List>
            </Stack>
          ),
        });
        return;
      }
      onError(t('teamRoles.deleteError'))(error);
    },
  });

  if (!isOn) return <Navigate to="/teams" replace />;

  const openModal = (role: TeamRoleDef | null) => {
    setEditing(role);
    setKey(role?.key ?? '');
    setName(role ? label(role) : '');
    setIsGroup(role?.isGroup ?? false);
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const normalizedKey = key.trim().toLowerCase();
  const keyError = editing
    ? null
    : !KEY_PATTERN.test(normalizedKey)
      ? t('teamRoles.keyInvalid')
      : roles.some((r) => r.key === normalizedKey)
        ? t('teamRoles.keyTaken')
        : null;
  // У роли по умолчанию пустое название возвращает название по умолчанию.
  const hasDefaultName = !!editing && DEFAULT_ROLE_KEYS.includes(editing.key);
  const nameError = name.trim() || hasDefaultName ? null : t('teamRoles.nameRequired');

  const handleSave = () => {
    setIsSubmitted(true);
    if (keyError || nameError) return;
    if (editing && name.trim() === label(editing) && isGroup === editing.isGroup) {
      setIsModalOpen(false);
      return;
    }
    save.mutate();
  };

  const confirmDelete = (role: TeamRoleDef) =>
    modals.openConfirmModal({
      title: t('teamRoles.deleteTitle', { name: label(role) }),
      centered: true,
      children: <Text size="sm">{t('teamRoles.deleteText')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(role),
    });

  const isLast = roles.length === 1;

  return (
    <PageContainer>
      <Group justify="space-between" mb="xs">
        <Title order={2}>{t('teamRoles.title')}</Title>
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={() => openModal(null)}
        >
          {t('teamRoles.add')}
        </Button>
      </Group>
      <Text c="dimmed" size="sm" mb={{ base: 'md', sm: 'xl' }} maw={600}>
        {t('teamRoles.intro')}
      </Text>
      <Stack gap="md" maw={600}>
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          roles.map((role) => (
            <Card key={role.key} shadow="sm" padding="md" radius="md" withBorder>
              <Group justify="space-between" wrap="nowrap">
                <div style={{ minWidth: 0 }}>
                  <Group gap="sm">
                    <Text size="md">{label(role)}</Text>
                    {role.isGroup && (
                      <Badge variant="light" color="violet">
                        {t('teamRoles.groupBadge')}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {t('teamRoles.keyLine', { key: role.key })}
                  </Text>
                </div>
                <Group gap="xs" wrap="nowrap">
                  <ActionIcon
                    variant="light"
                    color="teal"
                    aria-label={t('teamRoles.edit')}
                    onClick={() => openModal(role)}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <Tooltip label={t('teamRoles.lastRole')} disabled={!isLast}>
                    <ActionIcon
                      variant="light"
                      color="red"
                      aria-label={t('common.delete')}
                      disabled={isLast}
                      onClick={() => confirmDelete(role)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Card>
          ))
        )}
      </Stack>
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? t('teamRoles.edit') : t('teamRoles.add')}
      >
        <Stack>
          <TextInput
            label={t('teamRoles.name')}
            placeholder={
              hasDefaultName ? teamRoleName([], editing!.key, t) : t('teamRoles.namePlaceholder')
            }
            description={hasDefaultName ? t('teamRoles.nameResetHint') : undefined}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={isSubmitted && nameError}
            autoFocus
            onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
          />
          <TextInput
            label={t('teamRoles.key')}
            description={editing ? t('teamRoles.keyLocked') : t('teamRoles.keyHint')}
            placeholder="design"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            error={isSubmitted && keyError}
            disabled={!!editing}
            onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
          />
          <Switch
            label={t('teamRoles.isGroup')}
            description={canChangeGroup ? t('teamRoles.isGroupHint') : t('teamRoles.isGroupLocked')}
            disabled={!canChangeGroup}
            checked={isGroup}
            onChange={(e) => setIsGroup(e.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setIsModalOpen(false)} color="gray">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} color="teal" loading={save.isPending}>
              {editing ? t('common.save') : t('common.add')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContainer>
  );
};
